import React, { useState } from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Collapse,
    IconButton,
    Tooltip
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import BookmarkBorderIcon from '@material-ui/icons/BookmarkBorder'
import BookmarkIcon from '@material-ui/icons/Bookmark'

// State-medicaid DOB arrives as a YYYYMMDD digit string. Parse manually to avoid timezone drift.
const formatCompactDate = (raw) => {
    if (!raw) return null
    const s = String(raw).trim()
    if (s.length !== 8 || !/^\d{8}$/.test(s)) return s
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
}

// Suspension/reinstatement dates arrive as YYYY-MM-DD strings — pass through unchanged.
const formatIsoDate = (raw) => raw || null

const readIdentity = (record) => {
    const isEntity = !!record?.is_entity
    const displayName = isEntity
        ? (record.bus_name || '(unnamed entity)')
        : [record.first_name, record.mid_name, record.last_name]
            .filter(Boolean).join(' ').trim() || '(unnamed)'

    return {
        displayName,
        recordHash: record?.record_hash || null,
        isEntity,
        isActive: record?.is_active !== false,
        reinstatementDate: record?.reinstatement_date || null,
        stateCode: record?.state_code || null,
        sourceName: record?.source_name || null,
        city: record?.city || null,
        state: record?.state || null,
        npi: record?.npi || null,
        providerType: record?.provider_type || null,
        suspensionDate: record?.suspension_date || null,
        activePeriodRaw: record?.active_period_raw || null,
    }
}

// Reinstated badge — state Medicaid rows are kept visible with a prominent badge when the
// exclusion has been lifted. Do NOT hide reinstated rows.
const reinstatedBadge = (meta) => {
    if (meta.isActive) return null
    const formatted = formatIsoDate(meta.reinstatementDate)
    const style = {
        marginLeft: 6, padding: '1px 6px', fontSize: 10,
        borderRadius: 3, fontWeight: 600, verticalAlign: 'middle',
        background: 'var(--badge-approved-bg)', color: 'var(--status-approved)'
    }
    return (
        <span style={style} title='Exclusion was later lifted by the state Medicaid agency'>
            REINSTATED{formatted ? ` ${formatted}` : ''}
        </span>
    )
}

const ExpandableRow = ({ record, index, onSave, isSaved, saveEnabled }) => {
    const [open, setOpen] = useState(false)
    const meta = readIdentity(record)

    const savedTooltip = isSaved ? 'Already saved to this application' : 'Save this match to the application'
    const disabledTooltip = 'Only available inside an application'

    return (
        <>
            <TableRow hover style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
                <TableCell>
                    {meta.displayName}
                    {reinstatedBadge(meta)}
                </TableCell>
                <TableCell>{meta.isEntity ? 'Entity' : 'Individual'}</TableCell>
                <TableCell>{meta.stateCode || '—'}</TableCell>
                <TableCell>{meta.sourceName || '—'}</TableCell>
                <TableCell>{meta.npi || '—'}</TableCell>
                <TableCell>{meta.providerType || '—'}</TableCell>
                <TableCell>{formatIsoDate(meta.suspensionDate) || '—'}</TableCell>
                <TableCell>{meta.activePeriodRaw || '—'}</TableCell>
                <TableCell align='right' padding='none' style={{ paddingRight: 8, whiteSpace: 'nowrap' }}>
                    {onSave && (
                        <Tooltip title={saveEnabled ? savedTooltip : disabledTooltip} arrow>
                            <span>
                                <IconButton
                                    size='small'
                                    disabled={!saveEnabled || isSaved}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        if (!isSaved) onSave({ meta, record })
                                    }}
                                    aria-label={isSaved ? 'saved' : 'save'}
                                    id={`state-medicaid-save-${index}`}
                                >
                                    {isSaved ? <BookmarkIcon fontSize='small' color='primary'/> : <BookmarkBorderIcon fontSize='small'/>}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <IconButton
                        size='small'
                        aria-label={open ? 'collapse' : 'expand'}
                        id={`state-medicaid-toggle-${index}`}
                        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
                    >
                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={open} timeout='auto' unmountOnExit>
                        <div style={{ padding: '12px 16px', fontSize: 13, lineHeight: 1.6 }}>
                            <div>
                                <strong>Status:</strong> {meta.isActive ? 'Suspended' : `Reinstated${formatIsoDate(meta.reinstatementDate) ? ` on ${formatIsoDate(meta.reinstatementDate)}` : ''}`}
                                &nbsp;&nbsp; <strong>Type:</strong> {meta.isEntity ? 'Entity' : 'Individual'}
                            </div>
                            <div>
                                <strong>Source:</strong> {meta.sourceName || '—'}
                                &nbsp;&nbsp; <strong>State:</strong> {meta.stateCode || '—'}
                            </div>
                            <div>
                                <strong>Suspension Date:</strong> {formatIsoDate(meta.suspensionDate) || '—'}
                                &nbsp;&nbsp; <strong>Active period:</strong> {meta.activePeriodRaw || '—'}
                            </div>
                            {meta.providerType && (
                                <div><strong>Provider Type:</strong> {meta.providerType}</div>
                            )}
                            {meta.npi && (
                                <div><strong>NPI:</strong> {meta.npi}</div>
                            )}
                            {record.license_number && (
                                <div><strong>License #:</strong> {record.license_number}</div>
                            )}
                            {record.provider_numbers && (
                                <div><strong>Provider #s:</strong> {record.provider_numbers}</div>
                            )}
                            {record.aka_dba && (
                                <div><strong>AKA/DBA:</strong> {record.aka_dba}</div>
                            )}
                            {!meta.isEntity && record.dob && (
                                <div><strong>DOB:</strong> {formatCompactDate(record.dob)}</div>
                            )}
                            {(record.address || record.city || record.state || record.zip) && (
                                <div>
                                    <strong>Address:</strong>{' '}
                                    {[record.address, record.city, record.state, record.zip]
                                        .filter(Boolean).join(', ')}
                                </div>
                            )}
                            {record.additional_addresses && (
                                <div><strong>Additional addresses:</strong> {record.additional_addresses}</div>
                            )}
                            {record.exclusion_reason && (
                                <div><strong>Exclusion reason:</strong> {record.exclusion_reason}</div>
                            )}
                        </div>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

const StateMedicaidResultsTable = ({ records, onSave, savedKeys = new Set(), saveEnabled = false }) => {
    return (
        <TableContainer component={Paper} className='mt-3'>
            <Table size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>NPI</TableCell>
                        <TableCell>Provider Type</TableCell>
                        <TableCell>Suspension Date</TableCell>
                        <TableCell>Active Period</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {records.map((record, i) => {
                        const meta = readIdentity(record)
                        return (
                            <ExpandableRow
                                key={meta.recordHash || i}
                                record={record}
                                index={i}
                                onSave={onSave}
                                isSaved={savedKeys.has(meta.recordHash)}
                                saveEnabled={saveEnabled}
                            />
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default StateMedicaidResultsTable
